import {useWindowSize} from '@wandb/weave/common/hooks/useWindowSize';
import {makePropsAreEqual} from '@wandb/weave/common/util/shouldUpdate';
import {debounce, sum} from 'lodash';
import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';

import {TABLE_MIN_COLUMN_WIDTH} from '../../../util/constants';
import * as TableCols from '../../../util/tablecols';
import * as WBTreeHelpers from '../../../util/wbtree';
import {PROJECT_BAR_OFFSET} from '../../MultiRunWorkspace/constants';
import {
  type WBTableColumn,
  type WBTableGridContainerProps,
  type WBTableHoverCellCoords,
} from './types';
import {WBTableGrid} from './WBTableGrid';

const WBTableGridContainerComp: React.FC<WBTableGridContainerProps> = ({
  addFilter,
  addGroup,
  childrenPaginationMap,
  columnDragAccessor,
  columnDropAccessor,
  columnResizingAccessor,
  columns,
  expandable,
  expanded,
  expandedRowAddresses,
  fixedColumns,
  hideColumn,
  isSingleMode,
  isResizingFullHeight,
  loading,
  moveColumn,
  openSortPopup,
  pinnedTreeRef,
  readOnly,
  rows,
  pinnedRows,
  scrollXBounds,
  setChildrenPagination,
  setTableSettings,
  setTableState,
  showArtifactCounts,
  showBannerRowSpacer,
  showLogCounts,
  sort,
  tableSettings,
  toggleExpandedRow,
  togglePinnedColumn,
  topLevelQueryVariables,
  truncationType,
  unpinnedTreeRef,
  updateSort,
  updateTruncationType,
  useLeanGroupedRunsQuery,
}: WBTableGridContainerProps) => {
  const windowSize = useWindowSize();
  const [hoverCellCoords, setHoverCellCoords] =
    useState<WBTableHoverCellCoords>([]);

  // Users can move their mouse around really fast in the table, so throttle the
  // number of changes we make to hoverCellCoords to avoid excessive re-renders.
  // Note that this should probably be removed when we finally get on the React
  // concurrent-mode renderer.
  //
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  const throttledSetHoverCellCoords = useCallback(
    debounce(setHoverCellCoords, 30),
    []
  );

  const pinnedColumns = useMemo(
    () => columns.filter(c => TableCols.isPinned(tableSettings, c.accessor)),
    [tableSettings, columns]
  );

  const unPinnedColumns = useMemo(
    () => columns.filter(c => !TableCols.isPinned(tableSettings, c.accessor)),
    [tableSettings, columns]
  );

  const pinnedColumnsWidth = useMemo(
    () =>
      sum(
        pinnedColumns.map(c =>
          WBTreeHelpers.getColumnWidth(tableSettings, c.key)
        )
      ),
    [pinnedColumns, tableSettings]
  );

  // FIXME: the following code shouldn't happen as an effect of the render cycle
  //        instead, there should be a "reconcile changes to the table settings"
  //        function that, given a list of pinned columns, and a max width,
  //        figures out how to resize them if needed. That should then get
  //        called whenever pinned columns change, the window resizes, or the
  //        user resizes the run selector sidebar.

  // allow pinned columns to take up to 70% of available screen space.
  // if exceeded, automatically scale all pinned columns down to fit.
  // to prevent unnecessary re-renders, make sure to work with whole numbers.
  const pinnedColumnsWidthLimit = Math.floor(windowSize.width * 0.7);
  const pinnedColumnsMinWidth = pinnedColumns.length * TABLE_MIN_COLUMN_WIDTH;

  // HOWEVER:
  // We render the table without the pinned columns behavior if either:
  // 1. the screen size is below the tablet breakpoint
  // 2. the user has pinned all the visible columns
  // 3. the pinned columns are too wide (see above)
  //
  // In any of those cases, render the table with normal inline scroll --> `singleGridMode`.
  // But we only want to avoid the snap-back effect of 1 or 2  --> `singleGridRegardlessOfWidth`

  // The pinned columns are too wide if there isn't enough space to render at
  // least 4 minimum-sized columns in the unpinned section of the table.
  const pinnedColumnsAreTooWide =
    pinnedColumnsWidth >
    windowSize.width - (TABLE_MIN_COLUMN_WIDTH * 4 + PROJECT_BAR_OFFSET);

  const isCompacted = Boolean(expandable) && !expanded;
  const singleGridRegardlessOfWidth =
    (windowSize.responsiveSize === 'small' || unPinnedColumns.length === 0) &&
    !isCompacted;

  useEffect(() => {
    if (isResizingFullHeight || singleGridRegardlessOfWidth) {
      return;
    }
    if (
      pinnedColumnsWidth > pinnedColumnsWidthLimit &&
      pinnedColumnsWidthLimit > pinnedColumnsMinWidth
    ) {
      const reducedColumnWidths = {...tableSettings.columnWidths};
      pinnedColumns.forEach(column => {
        const curColumnWidth = WBTreeHelpers.getColumnWidth(
          tableSettings,
          column.key
        );
        const percentage = curColumnWidth / pinnedColumnsWidth;
        reducedColumnWidths[column.accessor] = Math.floor(
          Math.max(pinnedColumnsWidthLimit * percentage, TABLE_MIN_COLUMN_WIDTH)
        );
      });
      setTableSettings({
        columnWidths: reducedColumnWidths,
      });
    }
  }, [
    isResizingFullHeight,
    singleGridRegardlessOfWidth,
    pinnedColumns,
    pinnedColumnsMinWidth,
    pinnedColumnsWidth,
    pinnedColumnsWidthLimit,
    tableSettings,
    setTableSettings,
  ]);

  // Create a wrapper function so we can quickly render a pinned and unpinned table
  const renderTableBody = (opts: {
    cols: WBTableColumn[];
    pinned: boolean;
    leftMargin?: number;
    shouldSkipChildQuery?: boolean;
  }) => {
    return (
      <WBTableGrid
        addFilter={addFilter}
        addGroup={addGroup}
        childrenPaginationMap={childrenPaginationMap}
        columnDragAccessor={columnDragAccessor}
        columnDropAccessor={columnDropAccessor}
        columnResizingAccessor={columnResizingAccessor}
        columns={opts.cols}
        expandable={expandable}
        expanded={expanded}
        expandedRowAddresses={expandedRowAddresses}
        fixedColumns={fixedColumns}
        hideColumn={hideColumn}
        hoverCellCoords={hoverCellCoords}
        shouldSkipChildQuery={opts.shouldSkipChildQuery}
        isSingleMode={isSingleMode}
        leftMargin={opts.leftMargin || 0}
        loadingTable={loading}
        moveColumn={moveColumn}
        openSortPopup={openSortPopup}
        pinned={opts.pinned}
        readOnly={readOnly}
        rows={rows}
        pinnedRows={pinnedRows}
        scrollXBounds={opts.pinned ? [0, 10000] : scrollXBounds}
        setChildrenPagination={setChildrenPagination}
        setHoverCellCoords={throttledSetHoverCellCoords}
        setTableSettings={setTableSettings}
        setTableState={setTableState}
        showBannerRowSpacer={showBannerRowSpacer}
        showArtifactCounts={showArtifactCounts}
        showLogCounts={showLogCounts}
        sort={sort}
        tableSettings={tableSettings}
        toggleExpandedRow={toggleExpandedRow}
        togglePinnedColumn={togglePinnedColumn}
        topLevelQueryVariables={topLevelQueryVariables}
        treeRef={opts.pinned ? pinnedTreeRef : unpinnedTreeRef}
        truncationType={truncationType}
        updateSort={updateSort}
        updateTruncationType={updateTruncationType}
        useLeanGroupedRunsQuery={useLeanGroupedRunsQuery}
      />
    );
  };

  const singleGridMode = singleGridRegardlessOfWidth || pinnedColumnsAreTooWide;

  let tableBody: React.ReactNode;
  if (isCompacted) {
    // render pinned columns only
    tableBody = renderTableBody({
      cols: pinnedColumns,
      pinned: true,
    });
  } else if (singleGridMode) {
    // render unpinned columns only
    tableBody = renderTableBody({
      cols: columns,
      pinned: false,
    });
  } else {
    // render both pinned and unpinned columns
    tableBody = (
      <>
        {renderTableBody({
          cols: pinnedColumns,
          pinned: true,
        })}
        {renderTableBody({
          cols: unPinnedColumns,
          pinned: false,
          leftMargin: pinnedColumnsWidth,
          shouldSkipChildQuery: true,
        })}
      </>
    );
  }

  return tableBody;
};

export const WBTableGridContainer = memo(
  WBTableGridContainerComp,
  makePropsAreEqual({
    name: 'WBTableGridContainerMemo',
    deep: [
      'query',
      'scrollXBounds',
      'groupSelection',
      'groupTempSelection',
      'tempSelectedCount',
      'fixedColumns',
      'columns',
      'topLevelQueryVariables',
      'tableSettings',
      'sort',
    ],
    ignoreFunctions: true,
  })
);
